// =============================================================================
// Settings — Shared UI Primitives
// =============================================================================
// Building blocks usati da tutti i tab di Settings per garantire coerenza
// visiva (spaziature, focus ring emerald, stati vuoti, save bar, ecc.).
// =============================================================================

import { AlertCircle, CheckCircle, Loader2, Save } from 'lucide-react';
import { forwardRef } from 'react';

// -----------------------------------------------------------------------------
// Card
// -----------------------------------------------------------------------------

export interface SettingsCardProps {
  children: React.ReactNode;
  className?: string;
  /** Se true rimuove il padding interno (utile se il children gestisce padding) */
  flush?: boolean;
  /** Ancora per la navigazione a indice (vedi OrganizationTab) */
  id?: string;
}

export function SettingsCard({ children, className = '', flush = false, id }: SettingsCardProps) {
  return (
    <section
      id={id}
      className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow ${
        flush ? '' : 'p-6'
      } ${className}`}
    >
      {children}
    </section>
  );
}

// -----------------------------------------------------------------------------
// SectionHeader
// -----------------------------------------------------------------------------

export interface SectionHeaderProps {
  icon?: React.ElementType;
  title: string;
  description?: string;
  action?: React.ReactNode;
  /** Toni colore icona: emerald (default) o gray */
  tone?: 'emerald' | 'gray';
}

export function SectionHeader({
  icon: Icon,
  title,
  description,
  action,
  tone = 'emerald',
}: SectionHeaderProps) {
  const iconBg = tone === 'emerald' ? 'bg-emerald-50' : 'bg-gray-100';
  const iconColor = tone === 'emerald' ? 'text-emerald-600' : 'text-gray-600';
  return (
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="flex items-start gap-3 min-w-0">
        {Icon && (
          <div className={`p-2 rounded-lg flex-shrink-0 ${iconBg}`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
        )}
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 leading-snug">{title}</h3>
          {description && (
            <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{description}</p>
          )}
        </div>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// EmptyState
// -----------------------------------------------------------------------------

export interface EmptyStateProps {
  icon?: React.ElementType;
  title: string;
  description?: string;
  action?: React.ReactNode;
  /** Variante compatta per usare dentro card piccole */
  compact?: boolean;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  compact = false,
}: EmptyStateProps) {
  return (
    <div className={`text-center ${compact ? 'py-8' : 'py-12'}`}>
      {Icon && (
        <div className={`mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-100 ${
          compact ? 'w-12 h-12' : 'w-16 h-16'
        }`}>
          <Icon className={`text-gray-400 ${compact ? 'w-6 h-6' : 'w-8 h-8'}`} />
        </div>
      )}
      <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-gray-500 max-w-md mx-auto">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// LoadingBlock
// -----------------------------------------------------------------------------

export function LoadingBlock({ label }: { label?: string } = {}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <Loader2 className="w-7 h-7 animate-spin text-emerald-600" />
      {label && <p className="text-sm text-gray-500">{label}</p>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// ErrorBlock
// -----------------------------------------------------------------------------

export interface ErrorBlockProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}

export function ErrorBlock({ title = 'Si è verificato un errore', message, onRetry, retryLabel = 'Riprova' }: ErrorBlockProps) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-red-50 ring-1 ring-red-100 flex items-center justify-center">
        <AlertCircle className="w-6 h-6 text-red-500" />
      </div>
      <p className="text-gray-900 font-medium mb-1">{title}</p>
      <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 text-sm font-medium btn-primary"
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// FormField
// -----------------------------------------------------------------------------

export interface FormFieldProps {
  label: string;
  hint?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function FormField({ label, hint, required, className = '', children }: FormFieldProps) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
    </label>
  );
}

// -----------------------------------------------------------------------------
// Input (TextInput)
// -----------------------------------------------------------------------------

const inputBase =
  'w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg outline-none transition-shadow ' +
  'placeholder:text-gray-400 ' +
  'focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 ' +
  'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed';

// Attributi che disattivano l'autofill del browser e i prompt "ricorda
// indirizzo/dati" dei password manager (Chrome, 1Password, LastPass, Dashlane).
// Sono valori di default: ogni campo può sovrascriverli passando le proprie prop.
export const noAutofillProps = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
  'data-lpignore': 'true',
  'data-1p-ignore': '',
  'data-form-type': 'other',
} as const;

export const TextInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className = '', ...props }, ref) {
    return <input ref={ref} {...noAutofillProps} className={`${inputBase} ${className}`} {...props} />;
  },
);

export const TextArea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function TextArea({ className = '', rows = 3, ...props }, ref) {
    return <textarea ref={ref} rows={rows} className={`${inputBase} resize-y ${className}`} {...props} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className = '', children, ...props }, ref) {
    return (
      <select ref={ref} className={`${inputBase} pr-8 cursor-pointer ${className}`} {...props}>
        {children}
      </select>
    );
  },
);

// -----------------------------------------------------------------------------
// Toggle
// -----------------------------------------------------------------------------

export interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: string;
  'aria-label'?: string;
}

export function Toggle({ checked, onChange, disabled, label, ...rest }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={rest['aria-label'] ?? label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
        checked ? 'bg-emerald-600' : 'bg-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:ring-offset-2`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// -----------------------------------------------------------------------------
// Buttons
// -----------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ElementType;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500/40 disabled:bg-emerald-600/60',
  secondary:
    'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 focus-visible:ring-gray-300/60',
  danger:
    'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500/40 disabled:bg-red-600/60',
  ghost:
    'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-gray-300/60',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    icon: Icon,
    children,
    className = '',
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={`inline-flex items-center justify-center font-medium rounded-lg transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:cursor-not-allowed ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...rest}
    >
      {loading ? (
        <Loader2 className={size === 'sm' ? 'w-3.5 h-3.5 animate-spin' : 'w-4 h-4 animate-spin'} />
      ) : Icon ? (
        <Icon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
      ) : null}
      {children}
    </button>
  );
});

// IconButton: piccolo bottone icon-only riusato nelle liste (edit/delete/clock)
type IconTone = 'neutral' | 'primary' | 'danger' | 'blue';

const iconToneClasses: Record<IconTone, string> = {
  neutral: 'text-gray-400 hover:text-gray-700 hover:bg-gray-100',
  primary: 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50',
  blue: 'text-gray-400 hover:text-blue-600 hover:bg-blue-50',
  danger: 'text-gray-400 hover:text-red-600 hover:bg-red-50',
};

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ElementType;
  tone?: IconTone;
  label: string;
}

export function IconButton({ icon: Icon, tone = 'neutral', label, className = '', ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${iconToneClasses[tone]} ${className}`}
      {...rest}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

// -----------------------------------------------------------------------------
// Badge
// -----------------------------------------------------------------------------

type BadgeTone = 'green' | 'amber' | 'red' | 'blue' | 'purple' | 'gray' | 'emerald';

const badgeToneClasses: Record<BadgeTone, string> = {
  green: 'bg-green-100 text-green-700 ring-green-600/10',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  amber: 'bg-amber-100 text-amber-700 ring-amber-600/10',
  red: 'bg-red-100 text-red-700 ring-red-600/10',
  blue: 'bg-blue-100 text-blue-700 ring-blue-600/10',
  purple: 'bg-purple-100 text-purple-700 ring-purple-600/10',
  gray: 'bg-gray-100 text-gray-700 ring-gray-600/10',
};

export function Badge({
  tone = 'gray',
  children,
  className = '',
}: {
  tone?: BadgeTone;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${badgeToneClasses[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Feedback (inline saved/error message accanto a un'azione)
// -----------------------------------------------------------------------------

export interface FeedbackProps {
  type: 'success' | 'error';
  message: string;
}

export function Feedback({ type, message }: FeedbackProps) {
  const Icon = type === 'success' ? CheckCircle : AlertCircle;
  const color = type === 'success' ? 'text-emerald-700' : 'text-red-700';
  return (
    <div className={`flex items-center gap-2 text-sm ${color}`}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// -----------------------------------------------------------------------------
// SaveBar — barra di azione con feedback inline, sticky-friendly
// -----------------------------------------------------------------------------

export interface SaveBarProps {
  onSave: () => void;
  isSaving?: boolean;
  disabled?: boolean;
  saveLabel?: string;
  feedback?: FeedbackProps | null;
  /** Se true posiziona la barra in modalità sticky (fondo) */
  sticky?: boolean;
}

export function SaveBar({
  onSave,
  isSaving,
  disabled,
  saveLabel = 'Salva',
  feedback,
  sticky = false,
}: SaveBarProps) {
  return (
    <div
      className={`flex items-center gap-4 ${
        sticky
          ? 'sticky bottom-0 -mx-6 px-6 py-3 bg-white/95 backdrop-blur border-t border-gray-200 z-10'
          : 'mt-6'
      }`}
    >
      <Button onClick={onSave} loading={isSaving} disabled={disabled} icon={Save}>
        {saveLabel}
      </Button>
      {feedback && <Feedback {...feedback} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PageHeader (intestazione dei tab)
// -----------------------------------------------------------------------------

export interface TabPageHeaderProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export function TabPageHeader({ title, description, action }: TabPageHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
      <div className="min-w-0">
        <h2 className="text-xl font-semibold text-gray-900 leading-tight">{title}</h2>
        {description && (
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">{description}</p>
        )}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}
